import type { DialogStore } from '@ariakit/react'
import { useEffect, useState } from 'react'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Container } from '@/components/ui/container'
import { XCloseIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Tooltip } from '@/components/ui/tooltip'
import { formatLocaleDate } from '@/dates'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { Trans, useTranslation } from '@/translations/i18n'
import { ProPhotosConfirmSubmitModal } from './pro-photos-confirm-submit-modal'

interface ProPhotosHeaderProps {
	title: string
	description: string
	updatedAt?: string | null
	updatedBy?: number | null
	errorDescription: string
	isFieldsChanged: boolean
	onSubmit: () => void
	hasErrors: boolean
	isPending: boolean
	submitModalStore: DialogStore
}

export const ProPhotosHeader = ({
	title,
	description,
	updatedAt,
	updatedBy,
	errorDescription,
	isFieldsChanged,
	onSubmit,
	hasErrors,
	isPending,
	submitModalStore
}: ProPhotosHeaderProps) => {
	const { t } = useTranslation()
	const [showBanner, setShowBanner] = useState(false)

	useEffect(() => {
		if (!hasErrors && showBanner) {
			setShowBanner(false)
		}
	}, [hasErrors, showBanner])

	return (
		<>
			<Container fullHeight={false} fullWidth>
				<div className='flex items-center justify-between gap-2 pt-10 pb-6'>
					<div className='flex items-center gap-2'>
						<h1 className='font-medium text-lg text-neutral-900'>
							{title}
						</h1>
						<p className='text-base text-neutral-700'>
							{description}
						</p>
					</div>

					<div className='flex items-center gap-4'>
						{updatedAt && updatedBy && (
							<span className='text-neutral-900 text-sm'>
								<p>
									<Trans
										components={{
											strong: (
												<strong className='font-medium' />
											)
										}}
										i18nKey='vetting:reordering.last_updated_by'
										values={{ userId: updatedBy }}
									/>
								</p>
								<span className='text-neutral-700 text-xs'>
									{formatLocaleDate(updatedAt, 'PPp')}
								</span>
							</span>
						)}
						<Tooltip
							content={t('vetting:reordering.no_changes_to_save')}
							isOpenOnClickEnabled={false}
							rootProps={{
								open: isFieldsChanged ? false : undefined
							}}
							triggerProps={{ asChild: true }}
						>
							<ButtonBrandLegacy
								disabled={!isFieldsChanged}
								onClick={() =>
									hasErrors
										? setShowBanner(true)
										: submitModalStore.toggle()
								}
								size='sm'
							>
								{t('vetting:reordering.update_listing')}
							</ButtonBrandLegacy>
						</Tooltip>
					</div>
				</div>
				<ProPhotosConfirmSubmitModal
					isPending={isPending}
					onSubmit={onSubmit}
					store={submitModalStore}
				/>
			</Container>

			<Separator className='bg-neutral-300' orientation='horizontal' />

			{showBanner && (
				<SubHeaderBanner
					endSlot={
						<ButtonLegacy
							className='ml-auto [&_svg]:text-neutral-400'
							onClick={() => setShowBanner(false)}
							shape='square'
							size='sm'
							type='button'
							variant='tertiary'
						>
							<XCloseIcon
								className='fill-neutral-400'
								size={20}
							/>
						</ButtonLegacy>
					}
					subtitle={errorDescription}
					title={t('vetting:reordering.error_title')}
					type={'danger'}
				/>
			)}
		</>
	)
}
ProPhotosHeader.displayName = 'ProPhotosHeader'
